import {
  Box,
  Card,
  CardBody,
  Grid,
  GridItem,
  Heading,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
  HStack,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import Layout from "components/Layout";
import { useEntities } from "components/hooks/useEntities";
import { format } from "date-fns";
import { sortBy } from "lodash";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Inventory, Item, WithId } from "types";
import { MdPerson } from "react-icons/md";
import ItemAssignmentModal from "components/ItemAssignmentModal";
import Pagination from "components/Pagination";

const SORT = ["-created_at"];
const FIELDS = [
  "id",
  "change",
  "created_at",
  "balance",
  "request_id.requested_by.first_name",
  "request_id.requested_by.last_name",
  "transfer_id.requested_by.first_name",
  "transfer_id.requested_by.last_name",
];

const ITEM_FIELDS = [
  "*",
  "inventory_items.*",
  "assignments.*",
  "assignments.assigned_to.*",
];
const ITEM_DEEP = {
  inventory_items: { _limit: 1, _sort: "-created_at" },
  assignments: { _limit: 1, _sort: "-date_created" },
};
const PAGE_SIZE = 25;

export default function ItemDetails() {
  const { id } = useParams();
  const tableHeaderBackground = useColorModeValue("gray.200", "gray.900");

  const itemFilter = useMemo(() => ({ id: { _eq: id } }), [id]);
  const assignmentModal = useDisclosure();
  const { list: item, findAndUpdate } = useEntities<WithId<Item>>({
    entity: "Item",
    fields: ITEM_FIELDS,
    deep: ITEM_DEEP,
    filter: itemFilter,
    paginate: false,
  });

  const filter = useMemo(
    () => ({
      item_id: { _eq: id },
    }),
    [id]
  );

  const { list, isLoading, page, pages, setPage } = useEntities<
    WithId<Inventory>
  >({
    entity: "Inventory",
    sort: SORT,
    fields: FIELDS,
    filter,
    pageSize: PAGE_SIZE,
  });

  const getInitiator = (item: Inventory) => {
    if (item.request_id) {
      return `${item.request_id.requested_by.first_name} ${item.request_id.requested_by.last_name}`;
    }
    return `${item.transfer_id.requested_by.first_name} ${item.transfer_id.requested_by.last_name}`;
  };

  const getTransactionType = (item: Inventory) => {
    return item.request_id ? "Request" : "Transfer";
  };

  return (
    <Layout>
      <Box>
        {item[0] && (
          <Card mb={6}>
            <CardBody>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <Heading size="sm" mb={2}>
                    Name
                  </Heading>
                  <Text>{item[0].name}</Text>
                </GridItem>
                <GridItem>
                  <Heading size="sm" mb={2}>
                    Item Type
                  </Heading>
                  <Text>{item[0].item_type}</Text>
                </GridItem>
                <GridItem>
                  <Heading size="sm" mb={2}>
                    Available Units
                  </Heading>
                  <Text>
                    {item[0].assignments?.length
                      ? "--"
                      : item[0]?.inventory_items?.[0]?.balance ?? 0}
                  </Text>
                </GridItem>
                <GridItem>
                  <Heading size="sm" mb={2}>
                    Reorder Level
                  </Heading>
                  <Text>{item[0].reorder_level ?? "Not set"}</Text>
                </GridItem>
                {item[0].description && (
                  <GridItem colSpan={2}>
                    <Heading size="sm" mb={2}>
                      Description
                    </Heading>
                    <Text>{item[0].description}</Text>
                  </GridItem>
                )}
                {item[0].item_type === "Asset" && (
                  <>
                    <GridItem>
                      <Heading size="sm" mb={2}>
                        Current Assignee
                      </Heading>
                      <HStack>
                        <Text>
                          {item[0].assignments?.[0]
                            ? `${item[0].assignments[0].assigned_to.first_name} ${item[0].assignments[0].assigned_to.last_name}`
                            : "Unassigned"}
                        </Text>
                        {(item[0]?.inventory_items?.[0]?.balance ?? 0) !==
                          0 && (
                          <IconButton
                            aria-label="Assign"
                            icon={<MdPerson />}
                            size="sm"
                            colorScheme={
                              item[0].assignments?.length ? "green" : "gray"
                            }
                            onClick={assignmentModal.onOpen}
                          />
                        )}
                      </HStack>
                    </GridItem>
                    {item[0].assignments?.[0] && (
                      <GridItem>
                        <Heading size="sm" mb={2}>
                          Assignment Date
                        </Heading>
                        <Text>
                          {format(
                            new Date(item[0].assignments[0].date_created),
                            "yyyy-MM-dd"
                          )}
                        </Text>
                      </GridItem>
                    )}
                  </>
                )}
              </Grid>
            </CardBody>
          </Card>
        )}

        <Heading size="md" mb={4}>
          Item History
        </Heading>

        <VStack height={"100%"} alignItems="stretch">
          {isLoading && <Spinner alignSelf="center" />}

          {!isLoading && list.length === 0 && (
            <Text>No inventory records found for this item.</Text>
          )}

          {!isLoading && list.length > 0 && (
            <>
              <TableContainer>
                <Table variant="simple">
                  <Thead
                    position="sticky"
                    top={0}
                    zIndex={1}
                    bg={tableHeaderBackground}
                  >
                    <Tr>
                      <Th>By</Th>
                      <Th>Transaction Type</Th>
                      <Th isNumeric>Change</Th>
                      <Th isNumeric>Balance</Th>
                      <Th>Entry Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortBy(list, (v) => Number(v.id)).map((item) => (
                      <Tr key={item.id}>
                        <Td>{getInitiator(item)}</Td>
                        <Td>{getTransactionType(item)}</Td>
                        <Td
                          isNumeric
                          color={
                            item.change.startsWith("+")
                              ? "green.500"
                              : "red.500"
                          }
                        >
                          {item.change}
                        </Td>
                        <Td isNumeric>{item.balance}</Td>
                        <Td>
                          {format(new Date(item.created_at), "yyyy-MM-dd")}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {pages > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={pages}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </VStack>

        {item[0] && (
          <ItemAssignmentModal
            isOpen={assignmentModal.isOpen}
            onClose={assignmentModal.onClose}
            item={item[0]}
            onAssign={(val) => {
              const itemToUpdate = { ...item[0] };
              if (itemToUpdate.assignments) {
                itemToUpdate.assignments = [val, ...itemToUpdate.assignments];
                findAndUpdate(itemToUpdate.id, itemToUpdate);
              }
            }}
          />
        )}
      </Box>
    </Layout>
  );
}
